/**
 * Copyright (c) SAGE3 Development Team 2026. All Rights Reserved
 * University of Hawaii, University of Illinois Chicago, Virginia Tech
 *
 * Distributed under the terms of the SAGE3 License.  The full license is in
 * the file LICENSE, distributed as part of this software.
 */

import { useState, useEffect, useRef } from 'react';
import { Box, Text, Icon, useColorModeValue } from '@chakra-ui/react';
import { MdLock } from 'react-icons/md';

import { useHexColor } from '@sage3/frontend';
import { Board, Position, Size } from '@sage3/shared/types';
import { AppName } from '@sage3/applications/schema';

// Minimal app layout info needed to render the spatial preview
export type AppInfo = { position: Position; size: Size; type: AppName; id: string };

const PADDING = 2;

export function BoardPreview(props: { board: Board; width: number; height: number; isSelected?: boolean; appInfo: AppInfo[] }): JSX.Element {
  const [boardWidth, setBoardWidth] = useState(0);
  const [boardHeight, setBoardHeight] = useState(0);
  const [appsX, setAppsX] = useState(0);
  const [appsY, setAppsY] = useState(0);
  const [mapScale, setMapScale] = useState(1);

  const canvasRef = useRef<HTMLCanvasElement>(null);

  const boardColor = useHexColor(props.board.data.color);
  const appBorderColorValue = useColorModeValue('gray.700', 'gray.200');
  const appBorderColor = useHexColor(appBorderColorValue);
  const linearBGColor = useColorModeValue(
    `linear-gradient(172deg, #fafafa, #fbfbfb, #eeeeee)`,
    `linear-gradient(172deg, #2e2e2e, #313131, #292929)`
  );

  // Recompute layout whenever the appInfo prop changes
  useEffect(() => {
    const apps = props.appInfo;
    if (!apps || apps.length === 0) return;

    const appsLeft = apps.map((app) => app.position.x);
    const appsRight = apps.map((app) => app.position.x + app.size.width);
    const appsTop = apps.map((app) => app.position.y);
    const appsBottom = apps.map((app) => app.position.y + app.size.height);

    const width = Math.max(...appsRight) - Math.min(...appsLeft);
    const height = Math.max(...appsBottom) - Math.min(...appsTop);
    const scale = Math.min((props.width - 2 * PADDING) / width, (props.height - 2 * PADDING) / height) * 0.85;

    setBoardWidth(width * scale);
    setBoardHeight(height * scale);
    setAppsX(Math.min(...appsLeft));
    setAppsY(Math.min(...appsTop));
    setMapScale(scale);
  }, [props.appInfo, props.width, props.height]);

  // Redraw canvas when layout or colors change
  useEffect(() => {
    if (canvasRef.current && props.appInfo.length > 0) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        const dpr = window.devicePixelRatio || 1;
        const canvasWidth = boardWidth + 2 * PADDING;
        const canvasHeight = boardHeight + 2 * PADDING;
        canvas.width = canvasWidth * dpr;
        canvas.height = canvasHeight * dpr;
        canvas.style.width = `${canvasWidth}px`;
        canvas.style.height = `${canvasHeight}px`;
        ctx.scale(dpr, dpr);

        ctx.clearRect(0, 0, canvasWidth, canvasHeight);
        ctx.fillStyle = boardColor;
        ctx.strokeStyle = appBorderColor;
        ctx.lineWidth = 1;

        props.appInfo.forEach((app) => {
          const x = (app.position.x - appsX) * mapScale + PADDING;
          const y = (app.position.y - appsY) * mapScale + PADDING;
          const width = app.size.width * mapScale;
          const height = app.size.height * mapScale;
          ctx.fillRect(x, y, width, height);
          ctx.strokeRect(x, y, width, height);
        });
      }
    }
  }, [props.appInfo, boardColor, appBorderColor, mapScale, appsX, appsY, boardWidth, boardHeight]);

  return (
    <Box
      width={`${props.width}px`}
      height={`${props.height}px`}
      backgroundSize="contain"
      borderRadius="xl"
      background={linearBGColor}
      p="2"
      display="flex"
      alignItems="center"
      justifyContent="center"
      textAlign={'center'}
      flexDir={'column'}
    >
      {props.board.data.isPrivate ? (
        <>
          <Icon
            aria-label="LockBoard"
            fontSize="60px"
            pointerEvents="none"
            color={boardColor}
            m="0"
            p="0"
            _hover={{ cursor: 'initial' }}
            as={MdLock}
            textAlign={'center'}
            mb={2}
          />
          <Text fontWeight="bold" fontSize="xl" color={boardColor}>
            Private
          </Text>
        </>
      ) : props.appInfo.length > 0 ? (
        <canvas ref={canvasRef} style={{ width: `${boardWidth + 2 * PADDING}px`, height: `${boardHeight + 2 * PADDING}px` }} />
      ) : (
        <Text fontSize="xl" mb="2" color={boardColor} fontWeight="bold" css={{ textWrap: 'balance' }}>
          No Opened Applications
        </Text>
      )}
    </Box>
  );
}
